import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Avatar,
  Chip,
  Button,
  IconButton,
  Stack,
  Switch,
  FormControlLabel,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Fade,
  Backdrop,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Event as EventIcon,
  Inventory as TripIcon,
  Place as PlaceIcon,
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FilterList as FilterIcon,
  DateRange as DateIcon,
  AccessTime as TimeIcon,
  People as PeopleIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  FilterAlt as FilterAltIcon,
  ViewTimeline as TimelineIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon,
  Photo as PhotoIcon,
  ZoomIn as ZoomInIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { collection, query, where, getDocs, orderBy, Timestamp, addDoc, updateDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { format } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import OrgTimelineGallery from '../../../components/Organization/OrgTimelineGallery';
import TimelineItemDetailsDialog from '../../../components/Organization/TimelineItemDetailsDialog';

// Comment interface
interface Comment {
  id: string;
  text: string;
  createdAt: Date;
  createdBy: {
    id: string;
    name: string;
    photoUrl?: string;
  };
  likes: string[];
}

// Timeline item interface
interface TimelineItem {
  /**
   * Unique identifier for the timeline item
   */
  id: string;
  /**
   * Type of the timeline item (event or trip)
   */
  type: 'event' | 'trip';
  /**
   * Title of the timeline item
   */
  title: string;
  /**
   * Optional description of the timeline item
   */
  description?: string;
  /**
   * Start date of the timeline item
   */
  startDate: Date;
  /**
   * Optional end date of the timeline item
   */
  endDate?: Date;
  /**
   * Optional location of the timeline item
   */
  location?: string;
  /**
   * Optional destination of the timeline item (for trips)
   */
  destination?: string;
  /**
   * Optional cover image URL of the timeline item
   */
  coverImageUrl?: string;
  /**
   * Optional gallery images of the timeline item
   */
  galleryImages?: string[];
  /**
   * Optional color of the timeline item (for display purposes)
   */
  color?: string;
  /**
   * Information about the user who created the timeline item
   */
  createdBy: {
    id: string;
    name: string;
    photoUrl?: string;
  };
  /**
   * Optional number of participants for the timeline item
   */
  participants?: number;
  /**
   * Optional list of participants for the timeline item
   */
  participantsList?: {
    id: string;
    name: string;
    photoUrl?: string;
  }[];
  /**
   * ID of the organization that the timeline item belongs to
   */
  organizationId: string;
  /**
   * Optional list of comments for the timeline item
   */
  comments?: Comment[];
}

// Filter options interface
interface TimelineFilters {
  search: string;
  types: ('event' | 'trip')[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
}

const OrganizationTimelineView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeOrganization, isPersonalAccount, switchToOrganization } = useOrganization();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [showNewestFirst, setShowNewestFirst] = useState(true);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [filters, setFilters] = useState<TimelineFilters>({
    search: '',
    types: ['event', 'trip'],
    dateRange: { start: null, end: null }
  });
  
  // Menu state for item options
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  
  // New state variables for enhanced functionality
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentGalleryImages, setCurrentGalleryImages] = useState<string[]>([]);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [addTripDialogOpen, setAddTripDialogOpen] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);

  const [itemDetailsDialogOpen, setItemDetailsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TimelineItem | null>(null);

  // New state for event form
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    location: '',
    coverPhotoUrl: '',
  });
  const [eventLoading, setEventLoading] = useState(false);

  // New state for trip form
  const [newTrip, setNewTrip] = useState({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    destination: '',
    coverPhotoUrl: '',
  });
  const [tripLoading, setTripLoading] = useState(false);

  // Helper function to validate image URLs
  const isValidImageUrl = (url: string): boolean => {
    if (!url) return false;
    
    // Check if it's a valid URL format
    const isValidFormat = url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:image/');
    
    // Check if it's not just a document ID (which often causes 404s)
    const isProbablyDocId = /^[a-zA-Z0-9]{20,22}$/.test(url);
    
    if (!isValidFormat) {
      console.warn('Invalid image URL format:', url);
      return false;
    }
    
    if (isProbablyDocId) {
      console.warn('URL appears to be a document ID, not an image URL:', url);
      return false;
    }
    
    return true;
  };

  // Helper function to extract gallery images from various potential data structures
  const extractGalleryImages = (item: any): string[] => {
     const images: string[] = [];

     // Add cover photo if available
     if (
        item.coverPhotoUrl &&
        typeof item.coverPhotoUrl === 'string' &&
        isValidImageUrl(item.coverPhotoUrl)
     ) {
        images.push(item.coverPhotoUrl);
     }

     // Helper function to process arrays of potential image data
     const processImageArray = (arr: any[]) => {
        return arr
           .map((img) => {
              if (typeof img === 'string') {
                 return isValidImageUrl(img) ? img : null;
              }

              if (typeof img === 'object') {
                 // Handle common image object structures
                 let url = null;
                 if (img?.url) url = img.url;
                 else if (img?.imageUrl) url = img.imageUrl;
                 else if (img?.src) url = img.src;
                 else if (img?.path) url = img.path;
                 else if (img?.downloadURL) url = img.downloadURL;

                 // Check for downloadURL in specific frameworks
                 if (!url && img?.data?.downloadURL) url = img.data.downloadURL;

                 // Handle case where img might be a Firebase document reference with a downloadURL field
                 if (!url && typeof img.get === 'function') {
                    try {
                       url = img.get('downloadURL') || img.get('url') || img.get('imageUrl');
                    } catch (e) {
                       // Ignore errors from trying to access non-existent methods
                    }
                 }

                 return isValidImageUrl(url) ? url : null;
              }
              return null;
           })
           .filter(Boolean);
     };

     // Check various potential field names for images
     if (item.photos && Array.isArray(item.photos)) {
        const photoUrls = processImageArray(item.photos);
        images.push(...photoUrls);
     }

     if (item.images && Array.isArray(item.images)) {
        const imageUrls = processImageArray(item.images);
        images.push(...imageUrls);
     }

     if (item.gallery && Array.isArray(item.gallery)) {
        const galleryUrls = processImageArray(item.gallery);
        images.push(...galleryUrls);
     }

     if (item.assets && Array.isArray(item.assets)) {
        const assetUrls = processImageArray(item.assets);
        images.push(...assetUrls);
     }

     if (item.media && Array.isArray(item.media)) {
        const mediaUrls = processImageArray(item.media);
        images.push(...mediaUrls);
     }

     // Also check for photoUrls field which might be used in some events
     if (item.photoUrls && Array.isArray(item.photoUrls)) {
        const urls = processImageArray(item.photoUrls);
        images.push(...urls);
     }

     // Deduplicate using filter to avoid Set spread issues
     const uniqueImages = images.filter((url, index) => images.indexOf(url) === index);

     return uniqueImages;
  };

  useEffect(() => {
     // If we have an organization ID but we're in personal account mode,
     // switch to the organization
     if (id && isPersonalAccount) {
        switchToOrganization(id);
     }

     // Fetch timeline data
     if (id) {
        fetchTimelineData();
     }
  }, [id, isPersonalAccount, switchToOrganization]);

  const fetchTimelineData = async () => {
     if (!id) return;

     try {
        setLoading(true);

        // Fetch events
        const eventsRef = collection(db, 'events');
        const eventsQuery = query(
           eventsRef,
           where('organizationId', '==', id),
           orderBy('startDate', 'desc')
        );

        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData: TimelineItem[] = [];

        for (const doc of eventsSnapshot.docs) {
           const data = doc.data();

           // Fetch comments for this event
           let comments: Comment[] = [];
           try {
              const commentsRef = collection(db, 'events', doc.id, 'comments');
              const commentsQuery = query(commentsRef, orderBy('createdAt', 'desc'));
              const commentsSnapshot = await getDocs(commentsQuery);

              comments = commentsSnapshot.docs.map((commentDoc) => {
                 const commentData = commentDoc.data();
                 return {
                    id: commentDoc.id,
                    text: commentData.text,
                    createdAt:
                       commentData.createdAt instanceof Timestamp
                          ? commentData.createdAt.toDate()
                          : new Date(commentData.createdAt),
                    createdBy: {
                       id: commentData.createdBy.id,
                       name: commentData.createdBy.name,
                       photoUrl: commentData.createdBy.photoUrl,
                    },
                    likes: commentData.likes || [],
                 };
              });
           } catch (error) {
              console.error(`Error fetching comments for event ${doc.id}:`, error);
           }

           // Fetch participants details if available
           let participantsList: Array<{ id: string; name: string; photoUrl?: string }> = [];
           if (data.participants && Array.isArray(data.participants)) {
              participantsList = data.participants.map((participant: any) => ({
                 id: participant.id || participant,
                 name: participant.name || 'Participant',
                 photoUrl: participant.photoUrl,
              }));
           }

           // Get gallery images
           const galleryImages = extractGalleryImages(data);

           eventsData.push({
              id: doc.id,
              type: 'event',
              title: data.title,
              description: data.description,
              startDate:
                 data.startDate instanceof Timestamp
                    ? data.startDate.toDate()
                    : new Date(data.startDate),
              endDate:
                 data.endDate instanceof Timestamp
                    ? data.endDate.toDate()
                    : data.endDate
                      ? new Date(data.endDate)
                      : undefined,
              location: data.location?.name || data.location?.address,
              coverImageUrl: data.coverPhotoUrl,
              galleryImages,
              color: '#2196F3', // Blue for events
              createdBy: {
                 id: data.createdBy || data.userId,
                 name: data.createdByName || 'Team Member',
                 photoUrl: data.createdByPhotoUrl,
              },
              participants: data.participants?.length || 0,
              participantsList,
              organizationId: data.organizationId,
              comments,
           });
        }

        // Fetch trips
        const tripsRef = collection(db, 'trips');
        const tripsQuery = query(
           tripsRef,
           where('organizationId', '==', id),
           orderBy('startDate', 'desc')
        );

        const tripsSnapshot = await getDocs(tripsQuery);
        const tripsData: TimelineItem[] = [];

        for (const doc of tripsSnapshot.docs) {
           const data = doc.data();

           // Fetch comments for this trip
           let comments: Comment[] = [];
           try {
              const commentsRef = collection(db, 'trips', doc.id, 'comments');
              const commentsQuery = query(commentsRef, orderBy('createdAt', 'desc'));
              const commentsSnapshot = await getDocs(commentsQuery);

              comments = commentsSnapshot.docs.map((commentDoc) => {
                 const commentData = commentDoc.data();
                 return {
                    id: commentDoc.id,
                    text: commentData.text,
                    createdAt:
                       commentData.createdAt instanceof Timestamp
                          ? commentData.createdAt.toDate()
                          : new Date(commentData.createdAt),
                    createdBy: {
                       id: commentData.createdBy.id,
                       name: commentData.createdBy.name,
                       photoUrl: commentData.createdBy.photoUrl,
                    },
                    likes: commentData.likes || [],
                 };
              });
           } catch (error) {
              console.error(`Error fetching comments for trip ${doc.id}:`, error);
           }

           // Fetch travelers details if available
           let participantsList: Array<{ id: string; name: string; photoUrl?: string }> = [];
           if (data.travelers && Array.isArray(data.travelers)) {
              participantsList = data.travelers.map((traveler: any) => ({
                 id: traveler.id || traveler,
                 name: traveler.name || 'Traveler',
                 photoUrl: traveler.photoUrl,
              }));
           }

           // Get gallery images
           const galleryImages = extractGalleryImages(data);

           tripsData.push({
              id: doc.id,
              type: 'trip',
              title: data.title,
              description: data.description,
              startDate:
                 data.startDate instanceof Timestamp
                    ? data.startDate.toDate()
                    : new Date(data.startDate),
              endDate:
                 data.endDate instanceof Timestamp
                    ? data.endDate.toDate()
                    : data.endDate
                      ? new Date(data.endDate)
                      : undefined,
              destination: data.destination,
              location: data.destination,
              coverImageUrl: data.coverPhotoUrl,
              galleryImages,
              color: '#FF9800', // Orange for trips
              createdBy: {
                 id: data.createdBy,
                 name: data.createdByName || 'Team Member',
                 photoUrl: data.createdByPhotoUrl,
              },
              participants: data.travelers?.length || 0,
              participantsList,
              organizationId: data.organizationId,
              comments,
           });
        }

        // Combine and sort
        const combinedData = [...eventsData, ...tripsData];

        // Default: newest first
        combinedData.sort((a, b) =>
           showNewestFirst
              ? b.startDate.getTime() - a.startDate.getTime()
              : a.startDate.getTime() - b.startDate.getTime()
        );

        setTimelineItems(combinedData);
     } catch (error) {
        console.error('Error fetching timeline data:', error);
     } finally {
        setLoading(false);
     }
  };

  const handleToggleOrder = () => {
     setShowNewestFirst(!showNewestFirst);
     setTimelineItems((prevItems) => [...prevItems].reverse());
  };

  const handleToggleFilters = () => {
     setFiltersVisible(!filtersVisible);
  };

  const handleFilterChange = (key: keyof TimelineFilters, value: any) => {
     setFilters((prev) => ({
        ...prev,
        [key]: value,
     }));
  };

  const handleToggleItemType = (type: 'event' | 'trip') => {
     setFilters((prev) => {
        const currentTypes = [...prev.types];
        if (currentTypes.includes(type)) {
           return {
              ...prev,
              types: currentTypes.filter((t) => t !== type),
           };
        } else {
           return {
              ...prev,
              types: [...currentTypes, type],
           };
        }
     });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     setFilters((prev) => ({
        ...prev,
        search: e.target.value,
     }));
  };

  const handleItemOptionsClick = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
     setMenuAnchorEl(event.currentTarget);
     setSelectedItemId(itemId);
  };

  const handleItemOptionsClose = () => {
     setMenuAnchorEl(null);
     setSelectedItemId(null);
  };

  const handleViewItem = () => {
     const item = timelineItems.find((item) => item.id === selectedItemId);
     if (item) {
        navigate(`/organizations/${id}/${item.type}s/${item.id}`);
     }
     handleItemOptionsClose();
  };

  const handleEditItem = () => {
     const item = timelineItems.find((item) => item.id === selectedItemId);
     if (item) {
        navigate(`/organizations/${id}/${item.type}s/${item.id}/edit`);
     }
     handleItemOptionsClose();
  };

  const handleViewItemDetails = (item: TimelineItem) => {
     setSelectedItem(item);
     setItemDetailsDialogOpen(true);
  };

  const handleItemDetailsDialogClose = () => {
     setItemDetailsDialogOpen(false);
     setSelectedItem(null);
  };

  const filterTimelineItems = () => {
     return timelineItems.filter((item) => {
        // Filter by type
        if (!filters.types.includes(item.type)) {
           return false;
        }

        // Filter by search text
        if (
           filters.search &&
           !item.title.toLowerCase().includes(filters.search.toLowerCase()) &&
           !item.description?.toLowerCase().includes(filters.search.toLowerCase())
        ) {
           return false;
        }

        // Filter by date range (if specified)
        if (filters.dateRange.start && item.startDate < filters.dateRange.start) {
           return false;
        }

        if (filters.dateRange.end && item.startDate > filters.dateRange.end) {
           return false;
        }

        return true;
     });
  };

  const filteredItems = filterTimelineItems();

  // Group items by month/year for better visual organization
  const groupedItems: { [key: string]: TimelineItem[] } = {};

  filteredItems.forEach((item) => {
     const dateKey = format(item.startDate, 'MMMM yyyy');
     if (!groupedItems[dateKey]) {
        groupedItems[dateKey] = [];
     }
     groupedItems[dateKey].push(item);
  });

  const groupKeys = Object.keys(groupedItems);

  // If we want to ensure groups are in chronological order
  if (!showNewestFirst) {
     groupKeys.sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA.getTime() - dateB.getTime();
     });
  } else {
     groupKeys.sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateB.getTime() - dateA.getTime();
     });
  }

  const getDateRangeText = (startDate: Date, endDate?: Date) => {
     if (!endDate) {
        return format(startDate, 'MMM d, yyyy');
     }

     if (
        startDate.getMonth() === endDate.getMonth() &&
        startDate.getFullYear() === endDate.getFullYear()
     ) {
        return `${format(startDate, 'MMM d')} - ${format(endDate, 'd, yyyy')}`;
     }

     return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
  };

  const handleOpenGallery = (
     galleryImages: string[],
     itemId: string,
     itemType: 'event' | 'trip'
  ) => {
     // Make sure we have actual images to display
     if (!galleryImages.length && itemId) {
        // Try to find the item in our timelineItems array
        const item = timelineItems.find((i) => i.id === itemId);
        if (item?.coverImageUrl) {
           galleryImages = [item.coverImageUrl];
        }
     }

     // Filter out any empty or undefined URLs
     const validImages = galleryImages.filter((url) => Boolean(url));

     if (validImages.length === 0) {
        console.error('No valid images found for gallery');
        setSnackbarMessage('No images available for this item');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
     }

     setCurrentGalleryImages(validImages);
     setSelectedItemId(itemId);
     setGalleryOpen(true);
  };

  const handleOpenCommentDialog = (itemId: string) => {
    setSelectedItemId(itemId);
    setCommentDialogOpen(true);
    // Reset comment field
    setNewComment('');
    // Focus on comment field
    setTimeout(() => {
      if (commentInputRef.current) {
        commentInputRef.current.focus();
      }
    }, 100);
  };

  const handleAddComment = async () => {
    if (!selectedItemId || !newComment.trim() || !currentUser) {
      setSnackbarOpen(true);
      setSnackbarMessage('You must be logged in to comment');
      setSnackbarSeverity('error');
      return;
    }
    
    try {
      setSubmittingComment(true);
      
      const item = timelineItems.find(item => item.id === selectedItemId);
      if (item) {
        const commentsRef = collection(db, item.type + 's', item.id, 'comments');
        const newCommentDoc = await addDoc(commentsRef, {
          text: newComment,
          createdAt: serverTimestamp(),
          createdBy: {
            id: currentUser.uid,
            name: currentUser.displayName || 'Anonymous User',
            photoUrl: currentUser.photoURL || undefined
          },
          likes: []
        });
        
        const newCommentData: Comment = {
          id: newCommentDoc.id,
          text: newComment,
          createdAt: new Date(),
          createdBy: {
            id: currentUser.uid,
            name: currentUser.displayName || 'Anonymous User',
            photoUrl: currentUser.photoURL || undefined
          },
          likes: []
        };
        
        setTimelineItems(prevItems => {
          const updatedItems = [...prevItems];
          const itemIndex = updatedItems.findIndex(item => item.id === selectedItemId);
          if (itemIndex !== -1) {
            updatedItems[itemIndex].comments = [...(updatedItems[itemIndex].comments || []), newCommentData];
          }
          return updatedItems;
        });
        
        setSnackbarOpen(true);
        setSnackbarMessage('Comment added successfully!');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      setSnackbarOpen(true);
      setSnackbarMessage('Failed to add comment. Please try again.');
      setSnackbarSeverity('error');
    } finally {
      setSubmittingComment(false);
      setNewComment('');
      setCommentDialogOpen(false);
    }
  };

  const handleLikeComment = async (itemId: string, commentId: string) => {
    const item = timelineItems.find(item => item.id === itemId);
    if (item) {
      const commentsRef = collection(db, item.type + 's', item.id, 'comments');
      const commentRef = doc(commentsRef, commentId);
      const commentDoc = await getDoc(commentRef);
      if (commentDoc.exists()) {
        const commentData = commentDoc.data();
        const likes = commentData.likes || [];
        if (likes.includes(currentUser?.uid || '')) {
          likes.splice(likes.indexOf(currentUser?.uid || ''), 1);
        } else {
          likes.push(currentUser?.uid || '');
        }
        await updateDoc(commentRef, { likes });
        setTimelineItems(prevItems => {
          const updatedItems = [...prevItems];
          const itemIndex = updatedItems.findIndex(item => item.id === itemId);
          if (itemIndex !== -1) {
            if (updatedItems[itemIndex].comments) {
              const commentIndex = updatedItems[itemIndex].comments?.findIndex(comment => comment.id === commentId);
              if (commentIndex !== undefined && commentIndex !== -1) {
                updatedItems[itemIndex].comments![commentIndex].likes = likes;
              }
            }
          }
          return updatedItems;
        });
      }
    }
  };

  // Handle event form field changes
  const handleEventChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewEvent(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle event date changes
  const handleEventDateChange = (date: Date | null, field: 'startDate' | 'endDate') => {
    if (date) {
      setNewEvent(prev => ({
        ...prev,
        [field]: date
      }));
    }
  };

  // Handle trip form field changes
  const handleTripChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewTrip(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle trip date changes
  const handleTripDateChange = (date: Date | null, field: 'startDate' | 'endDate') => {
    if (date) {
      setNewTrip(prev => ({
        ...prev,
        [field]: date
      }));
    }
  };

  // Submit new event
  const handleAddEvent = async () => {
    try {
       setEventLoading(true);

       if (!newEvent.title) {
          setSnackbarMessage('Event title is required');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
       }

       if (!currentUser || !id) return;

       // Create new event using backend API
       const { createApiUrl } = await import('../../../utils/apiConfig');
       const { getAuthHeaders } = await import('../../../utils/auth');
       const apiUrl = createApiUrl('/api/events');
       const headers = await getAuthHeaders();

       const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
             title: newEvent.title,
             description: newEvent.description,
             startDate: newEvent.startDate.toISOString(),
             endDate: newEvent.endDate.toISOString(),
             location: newEvent.location,
             eventType: 'social',
             isPublic: false,
             coverImageUrl: newEvent.coverPhotoUrl,
             organizationId: id,
             userId: currentUser.uid,
          }),
       });

       const result = await response.json();

       if (!response.ok) {
          throw new Error(result.error || 'Failed to create event');
       }

       // Reset form and close dialog
       setNewEvent({
          title: '',
          description: '',
          startDate: new Date(),
          endDate: new Date(),
          location: '',
          coverPhotoUrl: '',
       });

       setAddEventDialogOpen(false);

       // Show success message
       setSnackbarMessage('Event created successfully');
       setSnackbarSeverity('success');
       setSnackbarOpen(true);

       // Refresh the timeline data
       fetchTimelineData();
    } catch (error) {
      console.error('Error creating event:', error);
      setSnackbarMessage('Error creating event');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setEventLoading(false);
    }
  };

  // Submit new trip
  const handleAddTrip = async () => {
    try {
       setTripLoading(true);

       if (!newTrip.title) {
          setSnackbarMessage('Trip title is required');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          return;
       }

       if (!currentUser || !id) return;

       // Create new trip using backend API
       const { createApiUrl } = await import('../../../utils/apiConfig');
       const { getAuthHeaders } = await import('../../../utils/auth');
       const apiUrl = createApiUrl('/api/trips');
       const headers = await getAuthHeaders();

       const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({
             title: newTrip.title,
             description: newTrip.description,
             startDate: newTrip.startDate.toISOString(),
             endDate: newTrip.endDate.toISOString(),
             isPublic: false,
             primaryDestination: newTrip.destination,
             coverPhotoUrl: newTrip.coverPhotoUrl,
             organizationId: id,
             userId: currentUser.uid,
          }),
       });

       const result = await response.json();

       if (!response.ok) {
          throw new Error(result.error || 'Failed to create trip');
       }

       // Reset form and close dialog
       setNewTrip({
          title: '',
          description: '',
          startDate: new Date(),
          endDate: new Date(),
          destination: '',
          coverPhotoUrl: '',
       });

       setAddTripDialogOpen(false);

       // Show success message
       setSnackbarMessage('Trip created successfully');
       setSnackbarSeverity('success');
       setSnackbarOpen(true);

       // Refresh the timeline data
       fetchTimelineData();
    } catch (error) {
      console.error('Error creating trip:', error);
      setSnackbarMessage('Error creating trip');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setTripLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {activeOrganization?.name || 'Organization'} Timeline
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleToggleOrder}
            startIcon={<TimelineIcon />}
          >
            {showNewestFirst ? 'Newest First' : 'Oldest First'}
          </Button>
          
          <Button
            variant="outlined"
            size="small"
            onClick={handleToggleFilters}
            startIcon={<FilterAltIcon />}
          >
            Filters
          </Button>
          
          <Button 
            variant="contained" 
            color="primary" 
            size="small"
            startIcon={<EventIcon />}
            onClick={() => setAddEventDialogOpen(true)}
            sx={{ ml: 1 }}
          >
            Add Event
          </Button>
          
          <Button 
            variant="contained" 
            color="secondary" 
            size="small"
            startIcon={<TripIcon />}
            onClick={() => setAddTripDialogOpen(true)}
          >
            Add Trip
          </Button>
        </Box>
      </Box>
      
      {/* Filters panel */}
      {filtersVisible && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search timeline..."
                value={filters.search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.types.includes('event')}
                      onChange={() => handleToggleItemType('event')}
                      color="primary"
                    />
                  }
                  label="Events"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.types.includes('trip')}
                      onChange={() => handleToggleItemType('trip')}
                      color="secondary"
                    />
                  }
                  label="Trips"
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No timeline items found
          </Typography>
          <Typography color="text.secondary" paragraph>
            {filters.search || !filters.types.includes('event') || !filters.types.includes('trip') ? 
              'Try adjusting your filters or search terms.' : 
              'Start by creating your first event or trip.'}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EventIcon />}
              onClick={() => setAddEventDialogOpen(true)}
            >
              Create Event
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<TripIcon />}
              onClick={() => setAddTripDialogOpen(true)}
            >
              Plan Trip
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {/* Timeline container */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: 'divider',
              transform: 'translateX(-1px)',
              zIndex: 0
            }}
          />
          
          {/* Timeline content */}
          <Stack spacing={4}>
            {groupKeys.map(monthYear => (
              <Box key={monthYear}>
                {/* Month/Year header */}
                <Paper
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: 'fit-content',
                    mx: 'auto',
                    px: 3,
                    py: 1,
                    mb: 3,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    boxShadow: 1
                  }}
                >
                  <Typography variant="h6" fontWeight="medium">
                    {monthYear}
                  </Typography>
                </Paper>
                
                {/* Month's timeline items */}
                <Stack spacing={3}>
                  {groupedItems[monthYear].map((item, index) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        position: 'relative',
                        zIndex: 1,
                        // Alternate items on left and right
                        flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                        justifyContent: 'center'
                      }}
                    >
                      {/* Content */}
                      <Box sx={{ width: '47%' }}>
                        <Card
                          elevation={2}
                          sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            // Subtle left/right border accent based on type
                            borderLeft: index % 2 === 0 ? `4px solid ${item.color}` : undefined,
                            borderRight: index % 2 !== 0 ? `4px solid ${item.color}` : undefined,
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 6,
                              transform: 'translateY(-4px)'
                            }
                          }}
                        >
                          {item.coverImageUrl && (
                            <CardMedia
                              component="img"
                              height="160"
                              image={item.coverImageUrl}
                              alt={item.title}
                            />
                          )}
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Chip
                                    icon={item.type === 'event' ? <EventIcon /> : <TripIcon />}
                                    label={item.type === 'event' ? 'Event' : 'Trip'}
                                    size="small"
                                    sx={{
                                      bgcolor: item.color,
                                      color: 'white',
                                      fontWeight: 500,
                                      mr: 1
                                    }}
                                  />
                                  {item.type === 'event' && item.participants && item.participants > 0 && (
                                    <Chip
                                      icon={<PeopleIcon fontSize="small" />}
                                      label={`${item.participants} ${item.participants === 1 ? 'Attendee' : 'Attendees'}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                                <Typography variant="h6" gutterBottom>
                                  {item.title}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => handleItemOptionsClick(e, item.id)}
                                aria-label="Options"
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Box>
                            {item.description && (
                              <Typography variant="body2" color="text.secondary" paragraph>
                                {item.description.length > 150
                                  ? `${item.description.substring(0, 150)}...`
                                  : item.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <DateIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                              <Typography variant="body2" color="text.secondary">
                                {getDateRangeText(item.startDate, item.endDate)}
                              </Typography>
                            </Box>
                            {item.location && (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PlaceIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {item.location}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                          <CardActions>
                            <Button 
                              size="small" 
                              startIcon={<InfoIcon />}
                              onClick={() => handleViewItemDetails(item)}
                            >
                              View Details
                            </Button>
                            <Button 
                              size="small" 
                              startIcon={<CommentIcon />}
                              onClick={() => handleOpenCommentDialog(item.id)}
                            >
                              Comments {item.comments?.length ? `(${item.comments.length})` : ''}
                            </Button>
                            {item.galleryImages && item.galleryImages.length > 0 && (
                              <Button 
                                size="small" 
                                onClick={() => handleOpenGallery(item.galleryImages || [], item.id, item.type)}
                                color="primary"
                                startIcon={<PhotoIcon />}
                              >
                                View Photos ({item.galleryImages.length})
                              </Button>
                            )}
                          </CardActions>
                        </Card>
                      </Box>
                      
                      {/* Timeline dot */}
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          border: '2px solid white',
                          boxShadow: 1,
                          mx: 2,
                          zIndex: 2
                        }}
                      />
                      
                      {/* Spacer for the other side */}
                      <Box sx={{ width: '47%' }} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      
      {/* Item options menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleItemOptionsClose}
      >
        <MenuItem onClick={handleViewItem}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditItem}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleItemOptionsClose}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Gallery */}
      {selectedItemId && (
        <OrgTimelineGallery 
          open={galleryOpen} 
          onClose={() => setGalleryOpen(false)} 
          images={currentGalleryImages} 
          itemId={selectedItemId || ''} 
          itemType={timelineItems.find(item => item.id === selectedItemId)?.type || 'event'} 
          organizationId={id || ''}
          onCommentAdded={() => fetchTimelineData()}
        />
      )}
      
      {/* Comment dialog */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '600px',
            borderRadius: 2,
            p: 4,
          },
        }}
      >
        <DialogTitle>
          Add Comment
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <List sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {selectedItemId && timelineItems.find(item => item.id === selectedItemId)?.comments?.length ? (
              timelineItems.find(item => item.id === selectedItemId)?.comments?.map(comment => (
                <ListItem 
                  key={comment.id}
                  secondaryAction={
                    <IconButton edge="end" aria-label="like" onClick={() => handleLikeComment(selectedItemId, comment.id)}>
                      <ThumbUpIcon color={comment.likes.includes(currentUser?.uid || '') ? "primary" : "action"} fontSize="small" />
                      {comment.likes.length > 0 && <Typography variant="caption" sx={{ ml: 0.5 }}>{comment.likes.length}</Typography>}
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar src={comment.createdBy.photoUrl} />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" component="span" fontWeight="medium">
                          {comment.createdBy.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(comment.createdAt, 'MMM d, yyyy h:mm a')}
                        </Typography>
                      </Box>
                    }
                    secondary={comment.text} 
                  />
                </ListItem>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No comments yet. Be the first to comment!
                </Typography>
              </Box>
            )}
          </List>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            inputRef={commentInputRef}
            placeholder="Type your comment..."
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button onClick={() => setCommentDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddComment}
            disabled={submittingComment}
          >
            {submittingComment ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Item details dialog */}
      <TimelineItemDetailsDialog 
        open={itemDetailsDialogOpen} 
        onClose={handleItemDetailsDialogClose} 
        item={selectedItem} 
        onViewGallery={handleOpenGallery}
        onEdit={(itemId, itemType) => navigate(`/organizations/${id}/${itemType}s/${itemId}/edit`)}
        onShare={() => {
          // You can implement share functionality here
          setSnackbarMessage("Sharing feature will be implemented soon!");
          setSnackbarSeverity("info");
          setSnackbarOpen(true);
        }}
      />
      
      {/* Add Event Dialog */}
      <Dialog
        open={addEventDialogOpen}
        onClose={() => setAddEventDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '600px',
            borderRadius: 2,
            p: 4,
          },
        }}
      >
        <DialogTitle>
          Add Event
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                value={newEvent.title}
                onChange={handleEventChange}
                name="title"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Description"
                value={newEvent.description}
                onChange={handleEventChange}
                name="description"
                multiline
                rows={4}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={newEvent.startDate.toISOString().split('T')[0]}
                onChange={(e) => handleEventDateChange(new Date(e.target.value), 'startDate')}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={newEvent.endDate.toISOString().split('T')[0]}
                onChange={(e) => handleEventDateChange(new Date(e.target.value), 'endDate')}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={newEvent.location}
                onChange={handleEventChange}
                name="location"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cover Photo URL"
                value={newEvent.coverPhotoUrl}
                onChange={handleEventChange}
                name="coverPhotoUrl"
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button onClick={() => setAddEventDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddEvent}
            disabled={eventLoading}
          >
            {eventLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Trip Dialog */}
      <Dialog
        open={addTripDialogOpen}
        onClose={() => setAddTripDialogOpen(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '600px',
            borderRadius: 2,
            p: 4,
          },
        }}
      >
        <DialogTitle>
          Add Trip
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trip Title"
                value={newTrip.title}
                onChange={handleTripChange}
                name="title"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trip Description"
                value={newTrip.description}
                onChange={handleTripChange}
                name="description"
                multiline
                rows={4}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={newTrip.startDate.toISOString().split('T')[0]}
                onChange={(e) => handleTripDateChange(new Date(e.target.value), 'startDate')}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={newTrip.endDate.toISOString().split('T')[0]}
                onChange={(e) => handleTripDateChange(new Date(e.target.value), 'endDate')}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                value={newTrip.destination}
                onChange={handleTripChange}
                name="destination"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cover Photo URL"
                value={newTrip.coverPhotoUrl}
                onChange={handleTripChange}
                name="coverPhotoUrl"
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button onClick={() => setAddTripDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddTrip}
            disabled={tripLoading}
          >
            {tripLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationTimelineView;